// Reusable payment-ledger modal. Given an order (and its bookingId), shows all
// CR/DR ledger rows for that booking (room order) or that order (walk-in), with
// totals. Read-only — just reads /history/booking/{id} or /history/order/{id}.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CircularProgress,
} from '@mui/material';
import config from '../../config';

const token = sessionStorage.getItem('token');
const auth = { Authorization: `Bearer ${token}` };

export default function PaymentLedgerModal({ open, onClose, orderId, bookingId }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [err, setErr] = useState('');

  const useBooking = Number(bookingId) > 0;

  const load = useCallback(() => {
    if (!open) return;
    const url = useBooking
      ? `${config.API_BASE_URL}/history/booking/${bookingId}`
      : `${config.API_BASE_URL}/history/order/${orderId}`;
    setLoading(true);
    setErr('');
    fetch(url, { headers: auth })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setRows(d.payments || []);
        setTotals(d.totals || null);
      })
      .catch(() => {
        setErr('Could not load payment ledger.');
        setRows([]);
        setTotals(null);
      })
      .finally(() => setLoading(false));
  }, [open, useBooking, bookingId, orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;
  const dt = (d) => (d ? new Date(d).toLocaleString('en-IN') : '—');

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 800, bgcolor: '#0a2540', color: '#fff' }}>
        💳 Payment Ledger — {useBooking ? `Booking #${bookingId}` : `Order #${orderId}`}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : err ? (
          <Typography color='error' sx={{ p: 2 }}>
            {err}
          </Typography>
        ) : (
          <>
            {totals && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip color='success' label={`Total CR (paid): ${fmt(totals.totalCr)}`} />
                <Chip color='warning' label={`Total DR (charge): ${fmt(totals.totalDr)}`} />
                <Chip
                  color={totals.balance > 0 ? 'error' : 'default'}
                  label={`Balance due: ${fmt(totals.balance)}`}
                />
              </Box>
            )}
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Date', 'Type', 'Amount', 'Mode', 'Ref / Card', 'Remark', 'By'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center' sx={{ py: 4, color: '#94a3b8' }}>
                      No payment entries.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt(p.date)}</TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          color={p.type === 'CR' ? 'success' : 'warning'}
                          label={p.type || '—'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{fmt(p.amount)}</TableCell>
                      <TableCell>{p.mode || '—'}</TableCell>
                      <TableCell>
                        {[p.refNo, p.cardType].filter(Boolean).join(' / ') || '—'}
                      </TableCell>
                      <TableCell>{p.remark || '—'}</TableCell>
                      <TableCell>{p.userName || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
